import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Switch, Route, Redirect, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import Home from "common/Home/Home";
import Auth from "features/auth/Auth";
import {
  AUTH_PATHS,
  EXTERNAL_INVITE_PATH,
  IS_TELEGRAM_WEB_APP,
  ONBOARDING_PATHS,
  UNIVERSAL_LINKS_PATHS,
} from "common/constants";
import DarkModeTheme from "@shared/common/Themes/DarkModeTheme";
import { ThemeProvider, createGlobalStyle } from "styled-components";
import AppTheme, { AppThemeType } from "@shared/common/Themes/AppTheme";
import styled from "styled-components";
import { selectStyle } from "common/Select/Select";
import useDarkMode from "utils/hooks/useDarkMode";
import useIntercom from "utils/hooks/useIntercom";
import { tooltipStyles } from "common/Tooltips/Tooltip";
import PrivateRoute from "common/Routes/PrivateRoute";
import { useSaveSearchParams } from "utils/hooks/useSearchParams";
import { rangePickerStyles } from "common/DatePicker/RangePicker";
import { modalStyles } from "common/Modal/Modal";
import usePageTracking from "utils/hooks/usePageTracking";
import RedirectWithQuery from "common/Routes/RedirectWithQuery";
import { selectIsDarkMode, selectWorkspace } from "@shared/slices/common.slice";
import { openMessage } from "utils/misc";
import { textAreaStyles } from "common/Inputs/TextArea";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import {
  useBottomNavigator,
  useDisplayLinkMessages,
  useTelegramSafeAreaInsets,
  useTelegramWebAppData,
} from "utils/hooks/misc";
import { useRestrictedCountries } from "utils/hooks/useRestrictedCountries";
import AppDownloadRedirect from "features/settings/AppDownloadRedirect";
import { LOGIN_UNIQUE_ID_STORAGE_KEY } from "@shared/common/constants";
import { v4 as uuidv4 } from "uuid";
import { sharedLib } from "@shared/services/shared.service";
import useInitializeWorkspace from "utils/hooks/useInitializeWorkspace";
import { drawerStyles } from "common/Drawers/Drawer";
import { popoverStyles } from "common/Popover/Popover";
import { Inset, WorkspaceType } from "@shared/types";
import { useUser } from "@shared/utils/hooks/misc";
import { useTheme } from "styled-components";
import AvoProvider from "analytics/AvoProvider";
import { dropdownStyles } from "common/Dropdown/Dropdown";
import { datePickerStyles } from "common/DatePicker/DatePicker.tsx";
import message from "antd/es/message";
import ConfigProvider from "antd/es/config-provider";
import { collapsedMenuPopoverItems } from "common/Sider/CollapsedMenu.ts";
import * as Sentry from "@sentry/react";
import QueriesError from "common/Empty/QueriesError.tsx";
import ExternalConnectAccount from "features/configuration/InviteExternalUsers/ExternalConnectAccount.tsx";
import { useWebViewBridge } from "services/webview/useWebViewBridge.ts";
import { timePickerStyles } from "common/DatePicker/TimeRangePicker.tsx";
import useHistoryTracker from "utils/hooks/useHistoryTracker.ts";
import { usePreventScroll } from "utils/hooks/usePreventScroll.ts";

const App = () => {
  const isDarkMode = useSelector(selectIsDarkMode);
  const selectedTheme = (isDarkMode ? DarkModeTheme : AppTheme) as AppThemeType;
  useWebViewBridge();
  usePreventScroll();
  useHistoryTracker();

  return (
    <ThemeProvider theme={selectedTheme}>
      <Sentry.ErrorBoundary
        fallback={<QueriesError isGeneralPageError={true} />}
      >
        <AvoProvider>
          <InnerApp />
        </AvoProvider>
      </Sentry.ErrorBoundary>
    </ThemeProvider>
  );
};

export default App;

const InnerApp = () => {
  useDarkMode();
  useIntercom();
  useSaveSearchParams();
  usePageTracking();
  useDisplayLinkMessages();
  useRestrictedCountries();
  useInitializeWorkspace();
  const { isInitialized: isTelegramWebAppInitialized, telegramStartValue } =
    useTelegramWebAppData();
  const { user } = useUser(true);
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const { safeAreaInset, contentSafeAreaInset } = useTelegramSafeAreaInsets();
  const [localIsLoaded, setLocalIsLoaded] = useState(false);
  const telegramStartValueRef = useRef(telegramStartValue);
  const token = useSelector(sharedLib.selectors.authentication.selectToken);
  const isLocalStorageTokenLoaded = useSelector(
    sharedLib.selectors.authentication.selectIsLoaded
  );
  const workspace = useSelector(selectWorkspace);
  const location = useLocation();
  const theme = useTheme();
  const colors = theme.colors;
  const isDarkMode = theme.isDarkMode;

  useEffect(() => {
    if (!localStorage.getItem(LOGIN_UNIQUE_ID_STORAGE_KEY)) {
      localStorage.setItem(LOGIN_UNIQUE_ID_STORAGE_KEY, uuidv4());
    }
  }, []);

  useEffect(() => {
    if (isLocalStorageTokenLoaded && workspace) {
      setLocalIsLoaded(true); // Storing value locally needed to persist through logouts which clear store
    }
  }, [isLocalStorageTokenLoaded, workspace]);

  const isLoading = !localIsLoaded || !isTelegramWebAppInitialized;

  const getContent = () => {
    if (isLoading) return;

    return (
      <Switch>
        <Route path={UNIVERSAL_LINKS_PATHS} component={AppDownloadRedirect} />
        <Route path={EXTERNAL_INVITE_PATH} component={ExternalConnectAccount} />
        <Route
          exact
          path={AUTH_PATHS}
          render={() => {
            if (ONBOARDING_PATHS.includes(location.pathname)) {
              return <Home />;
            }
            return token ? <RedirectWithQuery to="/" /> : <Auth />;
          }}
        />
        <Route
          exact
          path="/email-confirmed"
          render={() => {
            openMessage(
              "Your email has been confirmed successfully! You can now log in.",
              "success",
              8
            );
            return <Redirect to="/login" />;
          }}
        />
        <PrivateRoute
          path="/"
          render={() => (
            <Home telegramStartValue={telegramStartValueRef.current} />
          )}
        />
      </Switch>
    );
  };

  useLayoutEffect(() => {
    message.config({
      getContainer: () => document.getElementById("app")!,
    });
  }, []);

  return (
    <>
      {isBottomNavigator && (
        <IntercomStyles
          $hideLauncher={
            !user?.is_onboarding || workspace === WorkspaceType.ADMIN
          }
          $isWebApp={IS_TELEGRAM_WEB_APP}
        />
      )}
      <ConfigProvider
        getPopupContainer={() => {
          return document.getElementById("app")!;
        }}
        theme={{
          token: {
            colorPrimary: colors.primary,
            fontFamily: theme.typography.fontFamily,
          },
          components: {
            Segmented: {
              itemSelectedBg: colors.activeBgColor,
              trackBg: colors.tagBgColor,
              itemColor: isDarkMode ? "#ffffff" : "#292B33",
              itemSelectedColor: isDarkMode ? "#ffffff" : "#292B33",
              itemHoverColor: isDarkMode ? "#ffffff" : "#292B33",
            },
            Menu: {
              itemBg: colors.bgColor,
              itemHoverBg: "transparent",
              itemColor: colors.basic,
              itemHoverColor: isBottomNavigator
                ? colors.basic
                : colors.primaryBlue,
              itemBorderRadius: 0,
              itemSelectedBg: colors.lightBlue,
              itemSelectedColor: colors.primaryBlue,
              groupTitleColor: colors.secondaryGrey,
              popupBg: colors.cardBgColor,
              itemMarginInline: 0,
              itemMarginBlock: 0,
              subMenuItemSelectedColor: colors.primaryBlue,
              iconSize: 16,
            },
          },
        }}
      >
        <Wrapper
          id="app"
          $safeAreaInset={safeAreaInset}
          $contentSafeAreaInset={contentSafeAreaInset}
        >
          <LoadingSpinnerOverlay
            isLoading={!localIsLoaded || !isTelegramWebAppInitialized}
            hideContentWhileLoading={true}
          >
            {getContent()}
            {/*{!IS_TELEGRAM_WEB_APP && <MarketingWebsiteIframe />}*/}
          </LoadingSpinnerOverlay>
        </Wrapper>
      </ConfigProvider>
    </>
  );
};

const IntercomStyles = createGlobalStyle<{
  $hideLauncher?: boolean;
  $isWebApp?: boolean;
}>`
  ${(p) =>
    p.$isWebApp &&
    `
    .intercom-messenger-frame {
      background-color: black !important;
    }
    .intercom-messenger-frame iframe {
      box-sizing: border-box !important;
      padding-top: calc(var(--tg-safe-area-inset-top) + var(--tg-content-safe-area-inset-top)) !important;
      padding-bottom: var(--tg-safe-area-inset-bottom) !important;
    }
  `}

  ;

  ${(p) =>
    p.$hideLauncher &&
    `
    .intercom-launcher {
      display: none;
    }
  `}

  ;
`;

const Wrapper = styled.div<{
  $safeAreaInset?: Inset;
  $contentSafeAreaInset?: Inset;
}>`
  background: ${(p) => p.theme.colors.bgColor};
  min-height: 100vh;
  min-width: 100vw;
  ${(p) => p.theme.scrollbarStyle};
  ${dropdownStyles};
  ${popoverStyles};
  ${selectStyle};
  ${modalStyles};
  ${datePickerStyles};
  ${tooltipStyles};
  ${rangePickerStyles};
  ${collapsedMenuPopoverItems};
  ${drawerStyles};
  ${textAreaStyles};
  ${timePickerStyles};

  .ant-drawer.ant-drawer-left {
    transition: opacity 0.2s;

    &:not(.ant-drawer-open) {
      opacity: 0;
    }
  }

  .ant-message-top {
    top: ${(p) =>
      IS_TELEGRAM_WEB_APP
        ? (p.$safeAreaInset?.top ?? 0) + (p.$contentSafeAreaInset?.top ?? 0)
        : 0}px !important;
  }

  * {
    font-family: ${(p) => p.theme.typography.fontFamily};
  }
`;
